/**
 * "Goes great with" — behaviour for snippets/complementary-products.liquid.
 *
 * Three jobs:
 *   1. drag-to-scroll the card row, and only show the grab cursor when the
 *      cards actually overflow;
 *   2. clicking a card adds BOTH this product and the partner to the cart in a
 *      single request, then shows the toast (or drawer);
 *   3. attribute the sale — the partner line is stamped with the product it was
 *      suggested from, in the add payload. Underscore-prefixed properties are
 *      hidden from cart and drawer display.
 *
 * Lives in assets/ rather than inline in the snippet so tests can load the real
 * file and run it, which is the only way this stays honest — see
 * tests/complements.test.mjs.
 */
(function () {
  var root = document.querySelector('.cmp');
  if (!root) return;

  var SOURCE = root.getAttribute('data-source') || '';
  var rows = [].slice.call(root.querySelectorAll('.cmp__row'));

  function mark(row) {
    row.classList.toggle('is-draggable', row.scrollWidth > row.clientWidth + 1);
  }
  function markAll() { rows.forEach(mark); }
  markAll();
  window.addEventListener('resize', markAll);

  var dragged = false;
  rows.forEach(function (row) {
    var down = false, moved = false, startX = 0, startScroll = 0;
    row.addEventListener('mouseenter', function () { mark(row); });
    row.addEventListener('mousedown', function (e) {
      if (e.button !== 0) return;
      mark(row);
      down = true; moved = false;
      startX = e.pageX; startScroll = row.scrollLeft;
      row.classList.add('is-grabbing');
    });
    window.addEventListener('mousemove', function (e) {
      if (!down) return;
      var dx = e.pageX - startX;
      if (Math.abs(dx) > 4) { moved = true; dragged = true; }
      row.scrollLeft = startScroll - dx;
      e.preventDefault();
    });
    window.addEventListener('mouseup', function () {
      if (!down) return;
      down = false;
      row.classList.remove('is-grabbing');
    });
    // A drag that ends on a card must not also fire the add.
    row.addEventListener('click', function (e) {
      if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
    }, true);
  });

  /* ---- current variant --------------------------------------------------
     Read at click time, never cached: the shopper may change size after load,
     and the theme rewrites [name="id"] as a property (no MutationRecord). */
  function currentVariantId() {
    var el = document.querySelector('.product-variant-id')
          || document.querySelector('form [name="id"]')
          || document.querySelector('[name="id"]');
    return el && el.value ? String(el.value) : '';
  }

  function cartUrl(key, fallback) {
    return (window.routes && window.routes[key]) || fallback;
  }

  /* ---- add both ---------------------------------------------------------- */
  function addBoth(card, srcId, altId) {
    // Prefer the "Added to basket" toast, but only if it has upgraded (its JS is
    // deferred and may fail); otherwise fall back to the cart drawer.
    var toast = document.querySelector('cart-notification');
    var cart = (toast && typeof toast.renderContents === 'function') ? toast : document.querySelector('cart-drawer');
    var payload = {
      items: [
        { id: srcId, quantity: 1 },
        {
          id: altId, quantity: 1,
          properties: {
            _complement_source: SOURCE,
            _complement_rank: card.getAttribute('data-rank') || '',
          },
        },
      ],
    };
    // Ask for the toast's (or drawer's) sections back in the same round trip, exactly as
    // product-group.js does, so it can repaint without a second fetch.
    if (cart && cart.getSectionsToRender) {
      payload.sections = cart.getSectionsToRender().map(function (s) { return s.id; });
      payload.sections_url = window.location.pathname;
      if (cart.setActiveElement) cart.setActiveElement(document.activeElement);
    }

    card.classList.add('is-adding');
    fetch(cartUrl('cart_add_url', '/cart/add.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify(payload),
    })
      .then(function (r) { return r.json(); })
      .then(function (resp) {
        if (resp && resp.status) throw new Error(resp.description || 'cart rejected the add');
        if (cart && cart.renderContents) cart.renderContents(resp);
        else window.location.href = cartUrl('cart_url', '/cart');
      })
      .catch(function () {
        // Never dead-end: fall back to the partner's own product page.
        // the panel's confirm button is a <button>, so it carries data-href
        var href = card.getAttribute('href') || card.getAttribute('data-href');
        if (href) window.location.href = href;
      })
      .then(function () { card.classList.remove('is-adding'); });
  }

  function closePanels() {
    var ps = root.querySelectorAll('.cmp__panel');
    for (var i = 0; i < ps.length; i++) ps[i].setAttribute('hidden', '');
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia
              && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function openPanel(rank) {
    closePanels();
    var p = root.querySelector('.cmp__panel[data-panel="' + rank + '"]');
    if (!p) return null;
    p.removeAttribute('hidden');

    // Focus first. This is the accessible behaviour for a disclosure - keyboard
    // and screen-reader users have to land on what they just opened too, not
    // only sighted ones - and it makes Escape work without a further click.
    if (p.focus) p.focus();

    // Then reveal it, but ONLY if it is not already fully on screen. The block
    // sits under the short description, so a panel often opens below the fold;
    // when the strip happens to be mid-screen it is already visible, and
    // scrolling then would be disruption rather than help.
    if (p.getBoundingClientRect && p.scrollIntoView) {
      var r = p.getBoundingClientRect();
      var h = window.innerHeight || 0;
      var fullyVisible = r.top >= 0 && r.bottom <= h;
      if (!fullyVisible) {
        p.scrollIntoView({
          // 'nearest' moves the least distance that does the job; centring
          // would throw the product image off screen for no benefit.
          block: 'nearest',
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        });
      }
    }
    return p;
  }

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;

    // 1. the panel's own button is the only thing that adds to the cart
    var go = e.target.closest('.cmp__go');
    if (go && root.contains(go)) {
      e.preventDefault();
      e.stopPropagation();
      var goAlt = go.getAttribute('data-variant');
      var goSrc = currentVariantId();
      if (goAlt && goSrc) addBoth(go, goSrc, goAlt);
      return;
    }

    // 2. dismiss
    var close = e.target.closest('.cmp__close');
    if (close && root.contains(close)) {
      e.preventDefault();
      e.stopPropagation();
      closePanels();
      return;
    }

    // 3. a card REVEALS its pair panel - it never adds silently. Adding two
    //    products off one click was too much to infer from a card.
    var card = e.target.closest('.cmp__card');
    if (!card || !root.contains(card)) return;
    if (dragged) { dragged = false; return; }
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;

    var rank = card.getAttribute('data-rank');
    var altId = card.getAttribute('data-variant');
    // Nothing addable - let the link take them to the partner's product page.
    if (!altId || !rank) return;

    e.preventDefault();
    e.stopPropagation();
    openPanel(rank);
  }, true);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closePanels();
  });

})();
